import { useEffect, useRef, useState } from 'react'
import { useInView, useReducedMotion } from 'motion/react'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { ScrollReveal, TextReveal, useMediaQuery } from '@/components/motion'
import { HALFTONE_STYLE, Highlight } from '@/components/landing/about/Highlight'
import type { AIAgent, IndustryData, Workflow } from '@/data/industries'
import { lowerFirst, shortTime } from './listing'
import { stepAgent } from './WorkflowDiagram'

const TILE_BG = ['#ff00f2', 'var(--color-landing-accent)', 'var(--color-landing-secondary)', '#0a0a0a'] as const
const EASE = 'ease-[cubic-bezier(0.22,1,0.36,1)]'

const summary = (w: Workflow) =>
  `Starts when: ${lowerFirst(w.trigger)}. Ends with: ${lowerFirst(w.output)}. Saves ${w.timeSaved}.`

/** Tile art: the workflow's steps as a short stack of agent cards. */
function StepStack({ workflow, agents }: { workflow: Workflow; agents: AIAgent[] }) {
  return (
    <ol className="absolute right-[5%] top-[8%] grid w-[54%] gap-[2.2cqw] sm:w-[48%]">
      {workflow.steps.slice(0, 4).map((step, i) => {
        const agent = stepAgent(step, agents)
        return (
          <li
            key={`${step}-${i}`}
            className="flex min-w-0 items-center gap-[2cqw] bg-white px-[2.2cqw] py-[1.6cqw] shadow-[0_14px_24px_-16px_rgb(0_0_0_/_0.5)]"
            style={{ transform: `translateX(${(i % 2) * -6}%)` }}
          >
            <span className="flex h-[5.4cqw] w-[5.4cqw] shrink-0 items-center justify-center bg-[#0a0a0a] font-[family-name:var(--font-landing-display)] text-[max(8px,2.4cqw)] text-white">
              {agent ? agent.name[0] : i + 1}
            </span>
            <span className="min-w-0 truncate text-[max(9px,2.5cqw)] font-semibold text-[var(--color-landing-gray-800)]">{step}</span>
          </li>
        )
      })}
    </ol>
  )
}

function WorkflowTile({
  workflow,
  agents,
  index,
  bright,
  tileRef,
}: {
  workflow: Workflow
  agents: AIAgent[]
  index: number
  bright: boolean
  tileRef: (el: HTMLDivElement | null) => void
}) {
  return (
    <li>
      <div
        ref={tileRef}
        aria-hidden
        className={cn(
          'relative aspect-[3/2] overflow-hidden [container-type:inline-size] transition-opacity duration-700 dark:ring-1 dark:ring-inset dark:ring-white/10',
          EASE
        )}
        style={{ background: TILE_BG[index % TILE_BG.length], opacity: bright ? 1 : 0.5 }}
      >
        <div className="absolute inset-0 opacity-50" style={HALFTONE_STYLE} />
        <StepStack workflow={workflow} agents={agents} />
        <span className="absolute bottom-[5%] left-[5%] max-w-[90%] font-[family-name:var(--font-landing-display)] text-[clamp(1.75rem,8cqw,5.5rem)] uppercase leading-[1.1] tracking-[-0.03em] text-white">
          {shortTime(workflow.timeSaved)}
        </span>
      </div>
      {/* Copy, visible under each tile below lg where the slot is hidden. */}
      <div className="mt-5 lg:sr-only">
        <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold uppercase tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white">
          {workflow.name}
        </h3>
        <p className="mt-3 landing-text-body text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">{summary(workflow)}</p>
      </div>
    </li>
  )
}

/**
 * The about page's journey timeline, re-cut for an industry's workflows:
 * sticky heading column whose slot crossfades to the workflow in the
 * viewport's centre band; 3:2 tiles showing each workflow's agent steps and
 * its time saved; the 5px difference-blend centre line with a pink fill
 * whose head sticks to the viewport centre. Static flow below lg.
 */
export function WorkflowJourney({ industry }: { industry: IndustryData }) {
  const workflows = industry.workflows
  const desktop = useMediaQuery('(min-width: 1024px)')
  const reduce = useReducedMotion()
  const listRef = useRef<HTMLOListElement>(null)
  const tileRefs = useRef<(HTMLDivElement | null)[]>([])
  const near = useInView(listRef, { margin: '50% 0px 50% 0px' })
  const [active, setActive] = useState(0)
  const [inBand, setInBand] = useState(-1)

  useEffect(() => {
    if (!desktop || !near) return
    let frame = 0
    const measure = () => {
      frame = 0
      const mid = window.innerHeight / 2
      const half = window.innerHeight * 0.05
      let nextActive = 0
      let nextBand = -1
      tileRefs.current.forEach((el, i) => {
        if (!el) return
        const r = el.getBoundingClientRect()
        if (r.top <= mid) nextActive = i
        if (r.top < mid + half && r.bottom > mid - half) nextBand = i
      })
      setActive(nextActive)
      setInBand(nextBand)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [desktop, near])

  const dimmable = desktop && !reduce

  return (
    <section id="workflows" aria-labelledby="workflows-title" className="landing-section scroll-mt-20">
      <div className="landing-container grid items-start gap-14 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,1fr)] lg:gap-20">
        <div className="lg:sticky lg:top-28 lg:flex lg:h-[calc(100svh-9rem)] lg:flex-col">
          <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
            <span id="workflows-title">
              How the work <Highlight color="pink">flows.</Highlight>
            </span>
          </TextReveal>
          <ScrollReveal direction="none" delay={0.15}>
            <p className="mt-8 max-w-[34ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:border-[var(--color-landing-gray-700)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
              {workflows.length} workflows, each started by an everyday event and finished by a team of agents.
            </p>
            <Link
              to="/platform/ai-generation"
              className="group mt-10 inline-flex items-center gap-3 font-[family-name:var(--font-landing-display)] text-lg uppercase text-[var(--color-landing-gray-900)] transition-colors duration-200 hover:text-[var(--color-landing-accent)] dark:text-white"
            >
              How agents work
              <ArrowRight aria-hidden className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </ScrollReveal>

          {/* Crossfading slot (visual; the tiles carry the copy) */}
          <div aria-hidden className="mt-auto hidden pt-10 lg:grid">
            {workflows.map((w, i) => (
              <div
                key={w.name}
                className={cn('[grid-area:1/1] transition-[opacity,transform] duration-500', EASE)}
                style={{ opacity: i === active ? 1 : 0, transform: i === active ? 'translateY(0)' : 'translateY(10px)' }}
              >
                <p className="font-[family-name:var(--font-landing-display)] text-[1.6rem] uppercase leading-[1.4] tracking-[-0.02em] text-[var(--color-landing-gray-900)] dark:text-white">
                  {w.name}
                </p>
                <span className="mt-4 block h-px w-16 bg-[var(--color-landing-gray-300)] dark:bg-[var(--color-landing-gray-700)]" />
                <p className="mt-4 max-w-[42ch] landing-text-body text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
                  {summary(w)}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-1/2 z-10 hidden w-[5px] -translate-x-1/2 bg-white mix-blend-difference lg:block" />
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-1/2 z-20 hidden w-4 -translate-x-1/2 [overflow:clip] lg:block">
            <div className="sticky top-[50vh] h-0">
              <div className="absolute bottom-0 left-1/2 h-[200vh] w-[5px] -translate-x-1/2 bg-[#ff00f2]" />
              <div className="absolute left-1/2 top-0 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 bg-[#ff00f2]" />
            </div>
          </div>

          <ol ref={listRef} className="grid gap-16 lg:gap-24">
            {workflows.map((workflow, index) => (
              <WorkflowTile
                key={workflow.name}
                workflow={workflow}
                agents={industry.aiAgents}
                index={index}
                bright={!dimmable || index === inBand}
                tileRef={(el) => {
                  tileRefs.current[index] = el
                }}
              />
            ))}
          </ol>
        </div>
      </div>
    </section>
  )
}
